/**
 * As queries e mutations do módulo FinOps.
 *
 * 🔒 **Toda chamada leva `idCliente`**, e ele é o primeiro argumento posicional
 * de `chaveDeQuery` (BR-19). Não existe consulta deste módulo sem tenant — nem
 * no cache, nem no wire.
 *
 * 🔒 **Nenhuma tela filtra `visibilidadeCliente` no cliente.** O servidor devolve
 * o conjunto já filtrado (BR-01). Se alguma tela precisar esconder uma linha que
 * o servidor mandou, a regra está no lugar errado.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { apiUser } from '@/api/clients/apiUser'
import {
  envelopeDeLista,
  origemDiscoverySchema,
  servicoAwsSchema,
  statusDoCatalogoSchema,
} from '@/api/contracts/catalogo'
import { contaAwsSchema, respostaDeContasSchema, verificacaoSchema } from '@/api/contracts/contaAws'
import {
  respostaDeCustoSchema,
  dimensoesDisponiveisSchema,
  type ConsultaDeCusto,
} from '@/api/contracts/custos'
import { respostaDeDiscoverySchema, resultadoDaPromocaoSchema } from '@/api/contracts/discovery'
import { oportunidadeSchema, type Oportunidade } from '@/api/contracts/oportunidade'
import { resumoDoRelatorioSchema, serieDeEconomiaSchema } from '@/api/contracts/relatorio'
import { gerarRecomendacoesPorIa } from './aguardarExecucaoDiscoveryIa'
import { chaveDeQuery, chaveGlobal } from './chaveDeQuery'

/** Filtros da `2e` e da `2g`. Tudo opcional; o servidor aplica os defaults. */
// `| undefined` explícito em todo campo por causa de `exactOptionalPropertyTypes`:
// limpar um filtro é atribuir `undefined`, e sem isto o `setFiltros` de cada tela
// não compilaria — a alternativa seria montar o objeto condicionalmente em toda
// interação de filtro.
export interface FiltrosDeOportunidade {
  status?: string[] | undefined
  origem?: string[] | undefined
  accountId?: string[] | undefined
  servicoAws?: string[] | undefined
  visibilidadeCliente?: boolean | undefined
  lancamentoDe?: string | undefined
  lancamentoAte?: string | undefined
  propostaDe?: string | undefined
  propostaAte?: string | undefined
  implementacaoDe?: string | undefined
  implementacaoAte?: string | undefined
  busca?: string | undefined
  incluirArquivadas?: boolean | undefined
  ordenarPor?: string | undefined
  ordem?: 'asc' | 'desc' | undefined
  pagina?: number | undefined
  tamanho?: number | undefined
}

/**
 * Listas viram CSV, e não repetição do parâmetro.
 *
 * As duas formas funcionam no servidor; o CSV é escolhido porque a URL da `2g`
 * precisa ser compartilhável — um filtro de status com quatro valores repetidos
 * torna o link ilegível, e ilegível é o mesmo que não compartilhável.
 */
function paraParams(filtros: FiltrosDeOportunidade & { idCliente: string }) {
  const params: Record<string, string> = { idCliente: filtros.idCliente }
  for (const [chave, valor] of Object.entries(filtros)) {
    if (chave === 'idCliente' || valor === undefined || valor === null) continue
    if (Array.isArray(valor)) {
      if (valor.length > 0) params[chave] = valor.join(',')
    } else {
      params[chave] = String(valor)
    }
  }
  return params
}

const listaDeOportunidades = envelopeDeLista(oportunidadeSchema).extend({
  /** Gating de UX (BR-17). O servidor recusa a escrita de qualquer forma. */
  podeEditar: z.boolean(),
})

export function useOportunidades(idCliente: string, filtros: FiltrosDeOportunidade = {}) {
  return useQuery({
    queryKey: chaveDeQuery(idCliente, 'oportunidades', filtros as Record<string, unknown>),
    queryFn: async () =>
      listaDeOportunidades.parse(
        await apiUser.get<unknown>('/finops/oportunidades', {
          params: paraParams({ ...filtros, idCliente }),
        }),
      ),
  })
}

export function useOportunidade(idCliente: string, idOportunidade: string | undefined) {
  return useQuery({
    queryKey: chaveDeQuery(idCliente, 'oportunidade', idOportunidade),
    enabled: Boolean(idOportunidade),
    queryFn: async () =>
      oportunidadeSchema.parse(
        await apiUser.get<unknown>(`/finops/oportunidades/${idOportunidade}`, {
          params: { idCliente },
        }),
      ),
  })
}

export function useResumoDoRelatorio(idCliente: string, filtros: FiltrosDeOportunidade = {}) {
  return useQuery({
    queryKey: chaveDeQuery(idCliente, 'relatorio-resumo', filtros as Record<string, unknown>),
    queryFn: async () =>
      resumoDoRelatorioSchema.parse(
        await apiUser.get<unknown>('/finops/relatorio/resumo', {
          params: paraParams({ ...filtros, idCliente }),
        }),
      ),
  })
}

export function useEconomiaAcumulada(
  idCliente: string,
  de: string,
  ate: string,
  agruparPor: 'servico' | 'conta' | 'nenhum',
) {
  return useQuery({
    queryKey: chaveDeQuery(idCliente, 'economia-acumulada', de, ate, agruparPor),
    queryFn: async () =>
      serieDeEconomiaSchema.parse(
        await apiUser.get<unknown>('/finops/relatorio/economia-acumulada', {
          params: { idCliente, de, ate, agruparPor },
        }),
      ),
  })
}

// ── Discovery (tela 2d) ─────────────────────────────────────────────────────
export function useDiscovery(
  idCliente: string,
  filtros: { origem?: string[]; situacao?: string[] } = {},
) {
  return useQuery({
    queryKey: chaveDeQuery(idCliente, 'discovery', filtros as Record<string, unknown>),
    queryFn: async () =>
      respostaDeDiscoverySchema.parse(
        await apiUser.get<unknown>('/finops/discovery', {
          params: paraParams({ ...filtros, idCliente, tamanho: 200 }),
        }),
      ),
  })
}

export function usePromoverDiscovery(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (idsDiscovery: string[]) =>
      resultadoDaPromocaoSchema.parse(
        await apiUser.post<unknown>('/finops/discovery/promover', {
          idCliente,
          idsDiscovery,
        }),
      ),
    // As duas listas mudam: o feed perde as promovidas, e a gestão ganha os
    // rascunhos. Invalidar só uma deixaria a outra tela mentindo até o refresh.
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: chaveDeQuery(idCliente, 'discovery') })
      void client.invalidateQueries({ queryKey: chaveDeQuery(idCliente, 'oportunidades') })
    },
  })
}

export function useDescartarDiscovery(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (idsDiscovery: string[]) =>
      apiUser.post<unknown>('/finops/discovery/descartar', { idCliente, idsDiscovery }),
    onSuccess: () =>
      void client.invalidateQueries({ queryKey: chaveDeQuery(idCliente, 'discovery') }),
  })
}

/**
 * 🔒 BR-25 — a geração por IA é SEMPRE por clique, nunca em efeito de montagem.
 *
 * É `useMutation` e não `useQuery` justamente por isso: uma query dispara
 * sozinha ao montar, e é exatamente o que a regra proíbe. O controle de custo é
 * o motivo de a regra existir.
 *
 * Achado real (13/10/2026): o `onSuccess` disparava no 202 (aceito), não na
 * conclusão real do job — a lista era invalidada antes de o worker escrever
 * qualquer coisa, e um resultado de "0 sugestões novas" (válido, não erro)
 * ficava indistinguível de uma falha total. `gerarRecomendacoesPorIa` só
 * resolve quando o job termina de verdade (mesmo padrão do chat, ver
 * `aguardarExecucaoDiscoveryIa.ts`) — só então a lista é invalidada.
 */
export function useGerarRecomendacoesIa(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => gerarRecomendacoesPorIa(idCliente),
    onSuccess: () =>
      void client.invalidateQueries({ queryKey: chaveDeQuery(idCliente, 'discovery') }),
  })
}

// ── Contas AWS (tela 2b) ────────────────────────────────────────────────────
export function useContasAws(idCliente: string) {
  return useQuery({
    queryKey: chaveDeQuery(idCliente, 'contas'),
    queryFn: async () =>
      respostaDeContasSchema.parse(
        await apiUser.get<unknown>('/finops/contas', { params: { idCliente } }),
      ),
  })
}

export function useCadastrarConta(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (dados: { accountId: string; apelido: string }) =>
      contaAwsSchema.parse(await apiUser.post<unknown>('/finops/contas', { idCliente, ...dados })),
    onSuccess: () => void client.invalidateQueries({ queryKey: chaveDeQuery(idCliente, 'contas') }),
  })
}

/** O botão "tentar conexão novamente" da `2b`. */
export function useVerificarConta(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (accountId: string) =>
      contaAwsSchema.parse(
        await apiUser.post<unknown>(`/finops/contas/${accountId}/verificar`, { idCliente }),
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: chaveDeQuery(idCliente, 'contas') }),
  })
}

export function useHistoricoDeVerificacoes(idCliente: string, accountId: string | undefined) {
  return useQuery({
    queryKey: chaveDeQuery(idCliente, 'verificacoes', accountId),
    enabled: Boolean(accountId),
    queryFn: async () =>
      z.object({ itens: z.array(verificacaoSchema) }).parse(
        await apiUser.get<unknown>(`/finops/contas/${accountId}/verificacoes`, {
          params: { idCliente },
        }),
      ),
  })
}

// ── Cost Explorer (tela 2c) ─────────────────────────────────────────────────
export function useConsultaDeCustos(consulta: ConsultaDeCusto, habilitada = true) {
  return useQuery({
    queryKey: chaveDeQuery(
      consulta.idCliente,
      'custos',
      consulta as unknown as Record<string, unknown>,
    ),
    enabled: habilitada,
    queryFn: async () =>
      respostaDeCustoSchema.parse(await apiUser.post<unknown>('/finops/custos/consulta', consulta)),
  })
}

export function useDimensoesDeCusto(idCliente: string, campo: string) {
  return useQuery({
    queryKey: chaveDeQuery(idCliente, 'custos-dimensoes', campo),
    // ⚠️ Sem este guard, a sidebar da `2c` dispara SETE requisições com
    // `campo=` vazio no primeiro render — todas 400. `enabled` é o jeito do
    // TanStack Query de dizer "esta consulta ainda não faz sentido".
    enabled: Boolean(campo),
    queryFn: async () =>
      dimensoesDisponiveisSchema.parse(
        await apiUser.get<unknown>('/finops/custos/dimensoes', { params: { idCliente, campo } }),
      ),
  })
}

// ── Escritas de oportunidade (telas 2e e 2f) ────────────────────────────────
export function useSalvarOportunidade(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (dados: Partial<Oportunidade> & { idOportunidade?: string }) => {
      const { idOportunidade, ...campos } = dados
      const corpo = { idCliente, ...campos }
      const bruto = idOportunidade
        ? await apiUser.patch<unknown>(`/finops/oportunidades/${idOportunidade}`, corpo)
        : await apiUser.post<unknown>('/finops/oportunidades', corpo)
      return oportunidadeSchema.parse(bruto)
    },
    onSuccess: () => void invalidarOportunidades(client, idCliente),
  })
}

/**
 * A transição de status do BR-20.
 *
 * `campos` permite "preencher o que faltava E transicionar" numa chamada só — o
 * fluxo real da `2f`. Sem isso, o formulário teria que salvar, esperar, e só
 * então transicionar: duas requisições, e uma janela em que a oportunidade fica
 * salva mas na fase antiga.
 *
 * O erro `TRANSICAO_BLOQUEADA` traz `detalhes.camposFaltantes` com rótulo em
 * pt-BR — é o que permite marcar os campos exatos no formulário.
 */
export function useTransicionarStatus(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (params: {
      idOportunidade: string
      novoStatus: string
      campos?: Record<string, unknown>
    }) =>
      oportunidadeSchema.parse(
        await apiUser.post<unknown>(`/finops/oportunidades/${params.idOportunidade}/status`, {
          idCliente,
          novoStatus: params.novoStatus,
          campos: params.campos,
        }),
      ),
    onSuccess: () => void invalidarOportunidades(client, idCliente),
  })
}

/**
 * 🔒 O ATO do BR-01 — a única coisa que faz uma oportunidade curada chegar ao
 * Cliente. Endpoint próprio, e não um campo do PATCH, porque é auditado.
 */
export function useAlternarVisibilidade(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (params: { idOportunidade: string; visibilidadeCliente: boolean }) =>
      oportunidadeSchema.parse(
        await apiUser.put<unknown>(`/finops/oportunidades/${params.idOportunidade}/visibilidade`, {
          idCliente,
          visibilidadeCliente: params.visibilidadeCliente,
        }),
      ),
    onSuccess: () => void invalidarOportunidades(client, idCliente),
  })
}

/**
 * Invalida TUDO que depende de oportunidade — inclusive os relatórios.
 *
 * Publicar uma oportunidade muda os cinco MetricCards da `2g` e pode mudar o
 * histograma da `2h`. Invalidar só a lista deixaria os totais desatualizados na
 * mesma tela em que a linha acabou de mudar, que é exatamente o tipo de
 * inconsistência que ninguém reporta e todo mundo desconfia.
 */
function invalidarOportunidades(client: ReturnType<typeof useQueryClient>, idCliente: string) {
  return Promise.all([
    client.invalidateQueries({ queryKey: chaveDeQuery(idCliente, 'oportunidades') }),
    client.invalidateQueries({ queryKey: chaveDeQuery(idCliente, 'oportunidade') }),
    client.invalidateQueries({ queryKey: chaveDeQuery(idCliente, 'relatorio-resumo') }),
    client.invalidateQueries({ queryKey: chaveDeQuery(idCliente, 'economia-acumulada') }),
  ])
}

// ── Catálogos globais (BR-24) ───────────────────────────────────────────────
// `chaveGlobal` e não `chaveDeQuery`: os catálogos são os MESMOS para todos os
// clientes, e escopá-los por tenant faria N cópias idênticas no cache e N
// requisições onde uma basta.
export function useCatalogoDeStatus(idCliente: string) {
  return useQuery({
    queryKey: chaveGlobal('catalogo-status'),
    staleTime: 5 * 60_000,
    queryFn: async () =>
      z
        .object({ itens: z.array(statusDoCatalogoSchema) })
        .parse(await apiUser.get<unknown>('/finops/catalogos/status', { params: { idCliente } })),
  })
}

export function useCatalogoDeServicos(idCliente: string) {
  return useQuery({
    queryKey: chaveGlobal('catalogo-servicos'),
    staleTime: 5 * 60_000,
    queryFn: async () =>
      z
        .object({ itens: z.array(servicoAwsSchema) })
        .parse(await apiUser.get<unknown>('/finops/catalogos/servicos', { params: { idCliente } })),
  })
}

export function useCatalogoDeOrigens(idCliente: string) {
  return useQuery({
    queryKey: chaveGlobal('catalogo-origens'),
    staleTime: 5 * 60_000,
    queryFn: async () =>
      z.object({ itens: z.array(origemDiscoverySchema) }).parse(
        await apiUser.get<unknown>('/finops/catalogos/origens-discovery', {
          params: { idCliente },
        }),
      ),
  })
}
